import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, message, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, PauseCircleOutlined, PlayCircleOutlined, ReloadOutlined, SendOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { CreateButton, PaginationComponent, RefreshButton, TableComponent } from "@comps/components";
import { makeRequest } from "@utils/helpers";
import ScheduleReport from "./ScheduleReport";
import "../all-reports.css";
import {
  deleteReportScheduleReq,
  getReportScheduleRunsReq,
  getReportSchedulesReq,
  pauseReportScheduleReq,
  resumeReportScheduleReq,
  runReportScheduleNowReq,
} from "../requests";

const ScheduledReportsManagement = ({ onCountChange, modkey, scope }) => {
  const authUser = useSelector((state) => state.auth.authUser);
  const [form] = Form.useForm();
  const [loader, setLoader] = useState(false);
  const [actionLoader, setActionLoader] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [resources, setResources] = useState({});
  const [pagination, setPagination] = useState({ current_page: 1, total: 0, per_page: 15 });
  const [childComponent, setChildComponent] = useState(null);
  const [runsModal, setRunsModal] = useState({ open: false, schedule: null, runs: [] });

  useEffect(() => {
    getAllData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const reportOptions = useMemo(() => {
    return (resources.reports || []).map((report) => ({ value: report.id, label: report.title }));
  }, [resources.reports]);

  const columns = [
    {
      title: "Schedule",
      key: "name",
      render: (record) => (
        <div>
          <div style={ { fontWeight: 600 } }>{ record.name }</div>
          <div style={ { color: "#64748b", fontSize: 12 } }>{ record.report?.title }</div>
        </div>
      )
    },
    {
      title: "Repeat",
      key: "repeat",
      render: (record) => (
        <Space direction="vertical" size={ 0 }>
          <span>{ repeatLabel(record) }</span>
          <span style={ { color: "#64748b", fontSize: 12 } }>{ record.date_range_preset || "today" } · { (record.file_type || "xlsx").toUpperCase() }</span>
        </Space>
      )
    },
    // {
    //   title: "Recipients",
    //   key: "recipients",
    //   render: (record) => <span>{ recipientSummary(record) }</span>
    // },
    // {
    //   title: "Filters",
    //   key: "filters",
    //   render: (record) => {
    //     const summary = filterSummary(record);
    //     return (
    //       <Tooltip title={ summary.tooltip }>
    //         <Tag color={ summary.count ? "blue" : "default" }>{ summary.label }</Tag>
    //       </Tooltip>
    //     );
    //   }
    // },
    {
      title: "Status",
      key: "status",
      render: (record) => <Tag color={ record.status === "active" ? "green" : "default" }>{ record.status || "inactive" }</Tag>
    },
    {
      title: "Next Run",
      key: "next_run_at",
      render: (record) => record.next_run_at || "-"
    },
    {
      title: "Last Result",
      key: "last_result",
      render: (record) => {
        const run = record.latest_run || record.runs?.[0];
        if (!run) return record.last_error ? <Tag color="red">Failed</Tag> : "-";
        return <Tag color={ run.status === "sent" ? "green" : run.status === "failed" ? "red" : "blue" }>{ run.status }</Tag>;
      }
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 230,
      render: (record) => (
        <Space>
          <Tooltip title="Edit schedule">
            <Button size="small" icon={ <EditOutlined /> } onClick={ () => setChildComponent(<ScheduleReport schedule={ record } resources={ resources } scope={ scope } onCompleted={ onCompleted } />) } />
          </Tooltip>
          <Tooltip title="Queue this report now and email it when the file is ready">
            <Popconfirm title="Queue this report now? It will appear in the progress center and email after generation." onConfirm={ () => runNow(record) }>
              <Button size="small" icon={ <SendOutlined /> } />
            </Popconfirm>
          </Tooltip>
          <Tooltip title={ record.status === "active" ? "Pause" : "Resume" }>
            <Button
              size="small"
              icon={ record.status === "active" ? <PauseCircleOutlined /> : <PlayCircleOutlined /> }
              onClick={ () => toggleStatus(record) }
            />
          </Tooltip>
          <Tooltip title="Run history">
            <Button size="small" icon={ <ReloadOutlined /> } onClick={ () => openRuns(record) } />
          </Tooltip>
          <Tooltip title="Delete schedule">
            <Popconfirm title="Delete this schedule?" onConfirm={ () => deleteSchedule(record) }>
              <Button size="small" danger icon={ <DeleteOutlined /> } />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    },
  ];

  const getAllData = (page = pagination.current_page || 1) => {
    const values = form.getFieldsValue();
    makeRequest(setLoader, getReportSchedulesReq, {
      page,
      search: values.search,
      status: values.status,
      frequency: values.frequency,
      report_id: values.report_id,
      scope,
    }, onSuccess, onError);
  };

  const onSuccess = (response) => {
    const schedules = response.report_schedules || {};
    setDataSource(schedules.data || []);
    setPagination(schedules);
    onCountChange?.(schedules.total || 0);
    setResources(response.resources || resources);
  };

  const onError = (err, res) => {
    message.error(res?.response?.data?.message || "Unable to load scheduled reports");
  };

  const onCompleted = (changed) => {
    setChildComponent(null);
    if (changed) {
      getAllData();
    }
  };

  const runNow = (record) => {
    makeRequest(setActionLoader, runReportScheduleNowReq, { id: record.id, employment_id: authUser?.employment_id }, () => {
      message.success("Report queued. Check the progress center for file status.");
      getAllData();
    }, (err, res) => {
      message.error(res?.response?.data?.message || "Report run failed");
      getAllData();
    });
  };

  const toggleStatus = (record) => {
    const request = record.status === "active" ? pauseReportScheduleReq : resumeReportScheduleReq;
    makeRequest(setActionLoader, request, { id: record.id }, () => {
      message.success(record.status === "active" ? "Schedule paused" : "Schedule resumed");
      getAllData();
    }, (err, res) => {
      message.error(res?.response?.data?.message || "Unable to update schedule status");
    });
  };

  const deleteSchedule = (record) => {
    makeRequest(setActionLoader, deleteReportScheduleReq, { id: record.id }, () => {
      message.success("Schedule deleted");
      getAllData(1);
    }, (err, res) => {
      message.error(res?.response?.data?.message || "Unable to delete schedule");
    });
  };

  const openRuns = (record) => {
    makeRequest(setActionLoader, getReportScheduleRunsReq, { id: record.id }, (runs) => {
      setRunsModal({ open: true, schedule: record, runs: runs || [] });
    }, (err, res) => {
      message.error(res?.response?.data?.message || "Unable to load run history");
    });
  };

  const onFilter = () => getAllData(1);

  return (
    <>
      { childComponent }
      <div className="da-mb-16" style={ { display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: '5px' } }>
        <Space wrap>
          <RefreshButton onClick={ () => getAllData() } />
          <CreateButton onClick={ () => setChildComponent(<ScheduleReport resources={ resources } scope={ scope } onCompleted={ onCompleted } />) } text="Add Schedule" />
        </Space>
      </div>
      <Form form={ form } layout="inline" onFinish={ onFilter } className="da-mb-16">
        <Form.Item name="search">
          <Input placeholder="Search schedule or report" allowClear style={ { width: 240 } } />
        </Form.Item>
        <Form.Item name="report_id">
          <Select placeholder="Report" allowClear showSearch optionFilterProp="label" options={ reportOptions } style={ { width: 220 } } />
        </Form.Item>
        <Form.Item name="status">
          <Select placeholder="Status" allowClear style={ { width: 140 } } options={ [
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
          ] } />
        </Form.Item>
        <Form.Item name="frequency">
          <Select placeholder="Repeat" allowClear style={ { width: 140 } } options={ [
            { value: "daily", label: "Daily" },
            { value: "weekly", label: "Weekly" },
            { value: "monthly", label: "Monthly" },
            { value: "custom", label: "Custom" },
          ] } />
        </Form.Item>
        <Form.Item>
          <Button htmlType="submit" type="primary" className="reports-all__filter-apply">Apply</Button>
        </Form.Item>
      </Form>
      <TableComponent
        modkey={ modkey }
        loader={ loader || actionLoader }
        columns={ columns }
        dataSource={ dataSource }
        scroll={ { x: 1220 } }
      />
      <PaginationComponent
        pagination={ pagination }
        modkey={ modkey }
        currentPage={ pagination.current_page || 1 }
        setCurrentPage={ (page) => getAllData(page) }
      />
      <Modal
        title={ runsModal.schedule ? `${runsModal.schedule.name} - Run History` : "Run History" }
        open={ runsModal.open }
        onCancel={ () => setRunsModal({ open: false, schedule: null, runs: [] }) }
        footer={ null }
        width={ 900 }
      >
        <Table
          rowKey="id"
          size="small"
          dataSource={ runsModal.runs }
          pagination={ false }
          columns={ [
            { title: "Status", dataIndex: "status", render: (value) => <Tag color={ value === "sent" ? "green" : value === "failed" ? "red" : "blue" }>{ value }</Tag> },
            { title: "Triggered By", dataIndex: "triggered_by" },
            { title: "Started", dataIndex: "started_at" },
            { title: "Finished", dataIndex: "finished_at" },
            { title: "Recipients", dataIndex: "recipient_count" },
            { title: "Error", dataIndex: "error_message", render: (value) => value || "-" },
          ] }
        />
      </Modal>
    </>
  );
};

export default ScheduledReportsManagement;

function repeatLabel(record) {
  if (record.frequency === "weekly") return `Weekly on ${record.day_of_week || "Monday"}`;
  if (record.frequency === "monthly") return `Monthly on day ${record.day_of_month || 1}`;
  if (record.frequency === "custom") return record.custom_cron_expression || "Custom cron";
  return "Daily";
}

function recipientSummary(record) {
  const internalCount = record.recipients?.length || 0;
  const emailRow = record.email_recipients?.[0] || record.emails || {};
  const manualCount = normalizeEmailValue(emailRow.email_to || record.email_to).length;
  const ccCount = normalizeEmailValue(emailRow.email_cc || record.email_cc).length;
  const parts = [];
  if (internalCount) parts.push(`${internalCount} internal`);
  if (manualCount) parts.push(`${manualCount} manual`);
  if (ccCount) parts.push(`${ccCount} cc`);
  return parts.length ? parts.join(", ") : "No recipients";
}

function filterSummary(record) {
  const filters = record.filters && typeof record.filters === "object" ? record.filters : {};
  const optionalFilters = Object.entries(filters).filter(([key, value]) => !["from", "to"].includes(key) && value !== undefined && value !== null && value !== "");

  if (!optionalFilters.length) {
    return {
      count: 0,
      label: "No optional filters",
      tooltip: "This schedule uses the selected date range without department, branch, employee, or other optional report filters."
    };
  }

  const names = optionalFilters.map(([key]) => titleize(key)).join(", ");
  return {
    count: optionalFilters.length,
    label: `${optionalFilters.length} applied`,
    tooltip: `Applied optional filters: ${names}`
  };
}

function normalizeEmailValue(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const decoded = JSON.parse(value);
    return Array.isArray(decoded) ? decoded : [];
  } catch (e) {
    return String(value).split(/[,;\s]+/).filter(Boolean);
  }
}

function titleize(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
